import React, {ChangeEvent, useState, useEffect} from 'react'
import Alert from '@sharedComponents/Alert'
import {FormErrorType} from '@sharedComponents/PantaForm/types'
import {getAllErrors} from '@sharedComponents/PantaForm/utils'
import {ExtraPickupFormProps} from './types'
import {useTranslation} from 'react-i18next'
import {
    validateEmail,
    validatePhone,
    validateCustomerNr,
    validateCompanyName,
    validateStreetAddress,
    validateZipCode,
    validateCity,
    validateName,
    validateExtraContactName,
    validateNumberOfBags,
    validateExtraContactPhone,
    validateForm,
} from './validation'
import Button from '@sharedComponents/Button'
import {ButtonType} from '@sharedComponents/Button/types'
import some from 'lodash/some'
import {IExtraPickupFormBody} from '@apps/Shared/Services/Pickups/interfaces'
import InputText from "@sharedComponents/InputText";

const ExtraPickupForm = (props: ExtraPickupFormProps) => {
    const {t} = useTranslation()

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])
    const allErrors = getAllErrors(formErrors)
    const hasValidationErrors = !!(allErrors.length > 0)

    const [customerNumber, setCustomerNumber] = useState<string>('')
    const [companyName, setCompanyName] = useState<string>('')

    const [streetAddress, setStreetAddress] = useState<string>('')
    const [zipCode, setZipCode] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [numberOfBags, setNumberOfBags] = useState<string>('')

    const [name, setName] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [email, setEmail] = useState<string>('')

    const [extraContactName, setExtraContactName] = useState<string>('')
    const [extraContactPhone, setExtraContactPhone] = useState<string>('')

    const submitDisabled = !!(props.submitting || hasValidationErrors)

    const onChangeCustomerNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const regex = /^[0-9\b]+$/ // regex to match only numbers

        if (regex.test(value) || !value) {
            // if the input value is empty or matches the regex, update the state
            setCustomerNumber(value)
            const validationErrors = validateCustomerNr(value, formErrors)
            setFormErrors(validationErrors)
        }
    }

    const onChangeCompanyName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setCompanyName(value)
        const validationErrors = validateCompanyName(value, formErrors)
        setFormErrors(validationErrors)
    }

    const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setName(value)
        const validationErrors = validateName(value, formErrors)
        setFormErrors(validationErrors)
    }

    const onChangeExtraContactName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setExtraContactName(value)
        const validationErrors = validateExtraContactName(value, formErrors)
        setFormErrors(validationErrors)
    }

    const onChangeNumberOfBags = (e: ChangeEvent<HTMLInputElement>) => {
        const regex = /^[0-9\b]+$/ // regex to match only numbers
        const value = e.target.value

        if (regex.test(value) || !value) {
            const intValue = parseInt(value, 10)
            if (!isNaN(intValue)) {
                setNumberOfBags(intValue.toString())
                const validationErrors = validateNumberOfBags(
                    intValue.toString(),
                    formErrors
                )
                setFormErrors(validationErrors)
            } else {
                setNumberOfBags(value)
                const validationErrors = validateNumberOfBags(value, formErrors)
                setFormErrors(validationErrors)
            }
        }
    }

    const onChangeZipCode = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setZipCode(value)
        const errors = validateZipCode(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setCity(value)
        const errors = validateCity(value, formErrors)
        setFormErrors(errors)
    }

    const onChangeStreetAddress = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStreetAddress(value)
        const errors = validateStreetAddress(value, formErrors)
        setFormErrors(errors)
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setPhone(value)
        const validationErrors = validatePhone(value, formErrors)
        setFormErrors(validationErrors)
    }

    const onChangeExtraContactPhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setExtraContactPhone(value)
        const validationErrors = validateExtraContactPhone(value, formErrors)
        setFormErrors(validationErrors)
    }

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setEmail(value)
        const validationErrors = validateEmail(value, formErrors)
        setFormErrors(validationErrors)
    }

    const onSubmit = () => {
        const postBody: IExtraPickupFormBody = {
            customerNumber: customerNumber,
            companyName: companyName,
            streetAddress: streetAddress,
            zipCode: zipCode,
            city: city,
            numberOfBags: numberOfBags,
            name: name,
            phoneNumber: phone,
            emailAddress: email,
            extraContactName: extraContactName,
            extraContactphoneNumber: extraContactPhone,
        }

        const validationErrors = validateForm(postBody, formErrors)
        if (validationErrors.length > 0) {
            setFormErrors(validationErrors)
        } else {
            props.onSubmit(postBody)
        }
    }

    const resetFormValues = () => {
        setCustomerNumber('')
        setCompanyName('')

        setStreetAddress('')
        setCity('')
        setZipCode('')
        setNumberOfBags('')

        setName('')
        setPhone('')
        setEmail('')

        setExtraContactName('')
        setExtraContactPhone('')

        setFormErrors([])
    }

    useEffect(() => {
        resetFormValues()
    }, [props.resetFormToggle])

    return (
        <div className={'extra-pickup-form'}>
            <div className={'extra-pickup-form__section'}>
                <span className={'extra-pickup-form__section-heading'}>
                    {t('pickups.extraPickupForm.companyInformation')}
                </span>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-customerNumber"
                        label={t('global.customerNumber')}
                        value={customerNumber}
                        onChange={onChangeCustomerNumber}
                        required={true}
                        maxLength={7}
                        hasError={!!some(formErrors, ['key', 'customerNr'])}
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-companyName"
                        label={t('global.companyName')}
                        value={companyName}
                        onChange={onChangeCompanyName}
                        required={true}
                        hasError={!!some(formErrors, ['key', 'companyName'])}
                    />
                </div>
            </div>
            <div className={'extra-pickup-form__section'}>
                <span className={'extra-pickup-form__section-heading'}>
                    {t('pickups.extraPickupForm.pickupInformation')}
                </span>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-streetaddress"
                        label={t('global.streetAddress')}
                        hasError={!!some(formErrors, ['key', 'streetAddress'])}
                        value={streetAddress}
                        required={true}
                        onChange={onChangeStreetAddress}
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-zipcode"
                        label={t('global.zipCode')}
                        hasError={!!some(formErrors, ['key', 'zipCode'])}
                        value={zipCode}
                        required={true}
                        onChange={onChangeZipCode}
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-city"
                        label={t('global.city')}
                        hasError={!!some(formErrors, ['key', 'city'])}
                        value={city}
                        required={true}
                        onChange={onChangeCity}
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-numberOfBags"
                        label={t('pickups.extraPickupForm.numberOfBags')}
                        hasError={!!some(formErrors, ['key', 'numberOfBags'])}
                        value={numberOfBags}
                        required={true}
                        onChange={onChangeNumberOfBags}
                    />
                </div>
            </div>
            <div className={'extra-pickup-form__section'}>
                <span className={'extra-pickup-form__section-heading'}>
                    {t('pickups.extraPickupForm.primaryContact')}
                </span>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-contact-name"
                        label={t('global.name')}
                        value={name}
                        onChange={onChangeName}
                        required={true}
                        maxLength={50}
                        hasError={!!some(formErrors, ['key', 'name'])}
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-contact-phone"
                        label={t('global.phoneNumber')}
                        value={phone}
                        onChange={onChangePhone}
                        required={true}
                        maxLength={30}
                        hasError={!!some(formErrors, ['key', 'phone'])}
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-contact-email"
                        label={t('global.email')}
                        value={email}
                        onChange={onChangeEmail}
                        required={true}
                        hasError={!!some(formErrors, ['key', 'email'])}
                        maxLength={250}
                    />
                </div>
            </div>
            <div className={'extra-pickup-form__section'}>
                <span className={'extra-pickup-form__section-heading'}>
                    {t('pickups.extraPickupForm.extraContact')}
                </span>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-extra-contact-name"
                        label={t('global.name')}
                        value={extraContactName}
                        onChange={onChangeExtraContactName}
                        required={true}
                        maxLength={50}
                        hasError={
                            !!some(formErrors, ['key', 'extraContactName'])
                        }
                    />
                </div>
                <div className={'extra-pickup-form__input'}>
                    <InputText
                        id="extra-pickup-form-extra-contact-phone"
                        label={t('global.phoneNumber')}
                        value={extraContactPhone}
                        onChange={onChangeExtraContactPhone}
                        required={true}
                        maxLength={30}
                        hasError={
                            !!some(formErrors, ['key', 'extraContactPhone'])
                        }
                    />
                </div>
            </div>
            {hasValidationErrors && (
                <div className={'extra-pickup-form__error-messages'}>
                    <Alert
                        type="danger"
                        text={t('compensation.formErrors')}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            {props.requestErrors && props.requestErrors.length > 0 && (
                <div className={'extra-pickup-form__error-messages'}>
                    <Alert
                        type="danger"
                        text={t('error.somethingwrong')}
                        subtext={props.requestErrors}
                        compact={false}
                    />
                </div>
            )}

            <div className={'extra-pickup-form__button'}>
                <Button
                    type={ButtonType.PRIMARY}
                    text={t('button.submit')}
                    onClick={onSubmit}
                    disabled={submitDisabled}
                    loading={!!props.submitting}
                />
            </div>
        </div>
    )
}

export default ExtraPickupForm
